import { isPublisherResultImportActive } from '@/lib/publisher-result-imports';
import type {
    PublisherResultImport,
    PublisherResultImportStatus,
} from '@/types/publisher-result-import';

export const STATUS_META: Record<
    PublisherResultImportStatus,
    {
        label: string;
        description: string;
        variant: 'neutral' | 'info' | 'warning' | 'success' | 'destructive';
    }
> = {
    uploaded: {
        label: 'Recebido',
        description: 'Arquivo recebido e aguardando fila.',
        variant: 'neutral',
    },
    queued: {
        label: 'Na fila',
        description: 'Aguardando início do processamento.',
        variant: 'info',
    },
    extracting_text: {
        label: 'Lendo PDF',
        description: 'Extraindo texto selecionável do arquivo.',
        variant: 'info',
    },
    deidentifying: {
        label: 'Removendo identificação',
        description: 'Removendo dados pessoais antes da IA.',
        variant: 'info',
    },
    extracting_tables: {
        label: 'Extraindo tabelas',
        description: 'Identificando instrumentos e tabelas de resultado.',
        variant: 'info',
    },
    duplicate_warning: {
        label: 'Possível duplicado',
        description: 'Já existe uma importação similar para este paciente.',
        variant: 'warning',
    },
    ready_for_approval: {
        label: 'Pronto para revisar',
        description: 'Revise as tabelas extraídas antes de aprovar.',
        variant: 'success',
    },
    approved: {
        label: 'Aprovado',
        description: 'Importação aprovada e salva no histórico do paciente.',
        variant: 'success',
    },
    approved_pending_report: {
        label: 'Aguardando laudo',
        description: 'Importação aprovada e aguardando um laudo em andamento.',
        variant: 'warning',
    },
    failed: {
        label: 'Falhou',
        description: 'Não foi possível concluir esta importação.',
        variant: 'destructive',
    },
    cancelled: {
        label: 'Cancelado',
        description: 'Importação cancelada.',
        variant: 'neutral',
    },
    archived: {
        label: 'Arquivado',
        description: 'Importação arquivada.',
        variant: 'neutral',
    },
};

export const withEllipsis = (value: string) => `${value.replace(/\.$/, '')}...`;

/**
 * Where each status sits in the extraction pipeline. The backend reports a
 * stage, not a fraction, so the bar advances in steps — it will sit still on a
 * slow stage rather than animate through one, which is the honest reading.
 */
const STAGE_PROGRESS: Partial<Record<PublisherResultImportStatus, number>> = {
    uploaded: 20,
    queued: 20,
    extracting_text: 40,
    deidentifying: 60,
    extracting_tables: 80,
    ready_for_approval: 100,
    approved: 100,
    approved_pending_report: 100,
};

export const getImportProgress = (record: PublisherResultImport) =>
    STAGE_PROGRESS[record.status] ?? 0;

export const getBatchProgress = (records: PublisherResultImport[]) =>
    records.length === 0
        ? 0
        : Math.round(records.reduce((sum, r) => sum + getImportProgress(r), 0) / records.length);

/** Statuses that stall until the psychologist decides something. */
export const importNeedsAttention = (record: PublisherResultImport) =>
    record.status === 'duplicate_warning' || record.status === 'failed';

/**
 * How far along a batch is, as a single word. Used by the editor dock to decide
 * when to pull the psychologist back in: crossing out of `processing` is the
 * only transition worth interrupting them for.
 */
export type ImportsDockPhase = 'processing' | 'needs_attention' | 'ready';

/** The same classification, per record: what this one file is waiting on. */
export const getImportPhase = (record: PublisherResultImport): ImportsDockPhase => {
    if (importNeedsAttention(record)) return 'needs_attention';
    if (isPublisherResultImportActive(record)) return 'processing';

    return 'ready';
};

/**
 * What a dismissal is remembered against.
 *
 * The dock comes back when the batch changes in a way the psychologist has to
 * know about — a file crossed into `ready`/`needs_attention`, or a new file
 * joined. Moving *within* `processing` (Lendo PDF -> Extraindo tabelas) leaves
 * the signature alone, so a hidden dock stays hidden instead of blinking back
 * on every poll.
 */
export const getImportsDockSignature = (records: PublisherResultImport[]) =>
    records
        .map((record) => `${record.id}:${getImportPhase(record)}`)
        .sort()
        .join('|');

/**
 * The imports a report can call its own, newest first.
 *
 * Unlike the dock this keeps every status — `approved`, `cancelled` and
 * `archived` are the whole point of a history — and it is what the import
 * modal's "Histórico deste laudo" tab lists.
 */
export const selectReportImportHistory = (
    records: PublisherResultImport[],
    reportId: string | null | undefined,
) =>
    records
        .filter((record) => Boolean(reportId) && record.source_report_id === reportId)
        .sort((a, b) => {
            const byDate = Date.parse(b.created_at) - Date.parse(a.created_at);

            // Same-second uploads are the common case: one batch, many files.
            return byDate !== 0 ? byDate : a.id.localeCompare(b.id);
        });

export const formatImportDate = (value: string) =>
    new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));

export type ImportsDockAction = 'review' | 'review-duplicate' | 'retry';

export type ImportsDockSummary = {
    phase: ImportsDockPhase;
    tone: 'info' | 'warning' | 'destructive' | 'success';
    title: string;
    /** Short label for the collapsed pill, where the full title does not fit. */
    shortTitle: string;
    description: string;
    percent: number;
    cta: { label: string; action: ImportsDockAction } | null;
};

const pluralize = (count: number, singular: string, plural: string) =>
    count === 1 ? singular : plural;

/**
 * Collapses a batch into the one thing the dock should say about it.
 *
 * Precedence is worst-first — failed, then duplicate, then ready, then still
 * working. A batch where one file failed and two are fine is a batch that needs
 * the psychologist, and the header has to say so; the per-file list underneath
 * is what tells the rest of the story.
 */
export const getImportsDockSummary = (
    records: PublisherResultImport[],
): ImportsDockSummary | null => {
    if (records.length === 0) return null;

    const total = records.length;
    const percent = getBatchProgress(records);
    const failed = records.filter((record) => record.status === 'failed');
    const duplicates = records.filter((record) => record.status === 'duplicate_warning');

    if (failed.length > 0) {
        const [first] = failed;

        return {
            phase: 'needs_attention',
            tone: 'destructive',
            title:
                total === 1
                    ? 'Importação falhou'
                    : `${failed.length} de ${total} ${pluralize(failed.length, 'falhou', 'falharam')}`,
            shortTitle:
                total === 1
                    ? 'Importação falhou'
                    : `${failed.length} ${pluralize(failed.length, 'falhou', 'falharam')}`,
            description: first.safe_error_message || STATUS_META.failed.description,
            percent,
            cta: { label: 'Enviar novamente', action: 'retry' },
        };
    }

    if (duplicates.length > 0) {
        const [first] = duplicates;

        return {
            phase: 'needs_attention',
            tone: 'warning',
            title: 'Precisa de atenção',
            shortTitle: 'Precisa de atenção',
            description: `Possível duplicado — ${first.original_filename}`,
            percent,
            cta: { label: 'Revisar duplicado', action: 'review-duplicate' },
        };
    }

    if (records.every((record) => record.status === 'ready_for_approval')) {
        return {
            phase: 'ready',
            tone: 'success',
            title:
                total === 1
                    ? 'Resultado pronto para revisar'
                    : `${total} resultados prontos para revisar`,
            shortTitle: total === 1 ? '1 resultado pronto' : `${total} resultados prontos`,
            description: STATUS_META.ready_for_approval.description,
            percent,
            cta: { label: 'Revisar resultados', action: 'review' },
        };
    }

    // Whatever is furthest behind is what the batch is actually waiting on.
    const active = records.filter(isPublisherResultImportActive);
    const slowest = active.reduce(
        (behind, record) =>
            getImportProgress(record) < getImportProgress(behind) ? record : behind,
        active[0] ?? records[0],
    );

    return {
        phase: 'processing',
        tone: 'info',
        title: total === 1 ? records[0].original_filename : `Importando ${total} arquivos`,
        shortTitle: total === 1 ? 'Importando 1 arquivo' : `Importando ${total} arquivos`,
        description: withEllipsis(STATUS_META[slowest.status].description),
        percent,
        cta: null,
    };
};
